import React, { useState } from 'react';
import { 
    Dialog, 
    DialogContent, 
    DialogActions, 
    TextField, 
    Button, 
    Box, 
    IconButton, 
    InputAdornment
} from '@mui/material';
import { toast } from 'react-toastify';
import { addStops, addSTMSStops } from './action.ts'; 
import MapModal from '../../../WebApp/Pages/Warehouses/map-modal.tsx'; 
import Heading from '../../../WebApp/Components/Heading/heading.tsx';
import mapsIcon from '../../../assets/map.png';

interface AddStopsModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void; 
    vehicleId: string; 
    companiesId: string;
}

const AddStopsModal: React.FC<AddStopsModalProps> = ({ open, onClose, onSuccess, vehicleId, companiesId }) => {
    const [name, setName] = useState('');
    const [lat, setLat] = useState('');
    const [lng, setLng] = useState('');
    const [radius, setRadius] = useState(''); 
    const [isMapOpen, setIsMapOpen] = useState(false);
    const [nameError, setNameError] = useState('');
    const [latError, setLatError] = useState('');
    const [lngError, setLngError] = useState('');
    const [radiusError, setRadiusError] = useState('');

    const handleLocationSelect = (selectedAddress: string, selectedLat: string, selectedLng: string) => {
        setLat(selectedLat);
        setLatError('');
        setLng(selectedLng);
        setLngError(''); 
    };

    const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setRadius(value);

        if (value.trim() === '') {
            setRadiusError('Radius is required');
            return;
        }

        const hasInvalidChars = /[^\d.]/.test(value); 
        const dotCount = (value.match(/\./g) || []).length; 

        if (hasInvalidChars) {
            setRadiusError('Alphabets and special characters are not allowed');
        } else if (dotCount > 1) {
            setRadiusError('Only one decimal point is allowed');
        } else {
            setRadiusError('');
        }
    };

    const handleModalClose = () => {
        setNameError('');
        setLatError('');
        setLngError('');
        setRadiusError('');
        onClose();
    };

    const handleSubmit = () => {
        let isValid = true;

        if (!name.trim()) {
            setNameError("Stop Name is required");
            isValid = false;
        }
        if (!lat.trim()) {
            setLatError("Latitude is required");
            isValid = false;
        }
        if (!lng.trim()) {
            setLngError("Longitude is required");
            isValid = false;
        }
        if (!radius.trim()) {
            setRadiusError("Radius is required");
            isValid = false;
        } else if (radiusError) {
            isValid = false;
        }

        if (!isValid) return;

        const radiusValue = Number(radius);
        if (isNaN(radiusValue)) {
            setRadiusError("Radius must be a valid number");
            return;
        }
        addStops(
            companiesId,
            name,
            vehicleId, 
            lat,
            lng,
            radiusValue, 
            (response: any) => {
                if (response.status === 200 || response.status === 201) {
                    toast.success("Stop added successfully");
                    setName('');
                    setLat('');
                    setLng('');
                    setRadius('');
                    handleModalClose();
                    if (onSuccess) onSuccess(); 
                } else {
                    toast.error(response.message || "Failed to add stop");
                }
            },
            (errorMessage: string) => toast.error(errorMessage)
        );
    };

    const initialLocation = lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } : undefined;

    return (
        <>
            <Dialog open={open} onClose={handleModalClose} maxWidth="sm" >
                <Box sx={{ marginLeft: { xs: '0px', sm: '-180px' } }}>    
                    <Heading title="Add Stops" closeIcon="true" onClose={handleModalClose} />
                </Box>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField 
                            label="Stop Name" 
                            required
                            value={name} 
                            onChange={(e) => {
                                setName(e.target.value);
                                setNameError('');
                            }} 
                            error={!!nameError}
                            helperText={nameError}
                            sx={{ width: '210px' }}
                            size="small" 
                        />
                        
                        <TextField 
                            label="Latitude" 
                            required
                            value={lat} 
                            onChange={(e) => {
                                setLat(e.target.value);
                                setLatError('');
                            }}
                            error={!!latError}
                            helperText={latError}
                            placeholder="Auto-filled from map"
                            sx={{ width: '210px' }}
                            size="small" 
                        />
                        
                        <TextField 
                            label="Longitude"
                            required 
                            value={lng} 
                            onChange={(e) => {
                                setLng(e.target.value);
                                setLngError('');
                            }}
                            error={!!lngError}
                            helperText={lngError}
                            placeholder="Auto-filled from map"
                            sx={{ width: '210px' }}
                            size="small" 
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            onClick={() => setIsMapOpen(true)} 
                                            color="primary" edge="end" title="Select location on map"
                                        >
                                            <img src={mapsIcon} alt="Map View" style={{ width: '30px', height: '30px' }} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }} 
                        />

                        <TextField 
                            label="Radius (meters)" 
                            required
                            value={radius} 
                            onChange={handleRadiusChange}
                            type="text"
                            error={!!radiusError} 
                            helperText={radiusError} 
                            sx={{ width: '210px' }}
                            size="small" 
                        />
                    </Box>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'center' }}>
                    <Button onClick={handleModalClose} className="custom-cancel-button">Cancel</Button>
                    <Button onClick={handleSubmit} className="custom-button" variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            <MapModal 
                open={isMapOpen} 
                onClose={() => setIsMapOpen(false)} 
                onLocationSelect={handleLocationSelect}
                initialLocation={initialLocation}
            />
        </>
    );
};

export default AddStopsModal;